import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Loader2, Eye, EyeOff } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import MultiFileUpload from "@/components/MultiFileUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { inputCls, labelCls, Field, Toggle } from "./ui";
import { toast } from "sonner";

const TYPE_LABELS = { writing: "Scrittura", video: "Video", short: "Short Video", audio: "Audio / Podcast" };
const MEDIA_KINDS = ["none", "upload", "youtube", "vimeo", "spotify", "soundcloud"];

const blank = (type) => ({
  type, title: "", excerpt: "", body: "", cover_path: null, media_kind: type === "writing" ? "none" : "youtube",
  media_url: "", media_path: null, tags: [], lang: "it", status: "draft", is_premium: false, featured: false, reading_time: null, attachments: [],
});

export default function ContentManager({ type }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(0);
  const trackBusy = (b) => setUploading((n) => Math.max(0, n + (b ? 1 : -1)));

  const load = useCallback(() => {
    api.get("/admin/content", { params: { type } }).then((r) => setItems(r.data)).catch(() => {});
  }, [type]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    const payload = { ...editing, tags: Array.isArray(editing.tags) ? editing.tags : String(editing.tags || "").split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editing.id) await api.put(`/admin/content/${editing.id}`, payload);
      else await api.post("/admin/content", payload);
      toast.success("Salvato");
      setEditing(null);
      load();
    } catch { toast.error("Errore salvataggio"); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo contenuto?")) return;
    await api.delete(`/admin/content/${id}`);
    toast.success("Eliminato");
    load();
  };

  const accept = type === "audio" ? "audio/*" : "video/*";

  return (
    <div data-testid={`cms-content-${type}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl">{TYPE_LABELS[type]}</h2>
        <button onClick={() => setEditing(blank(type))} data-testid="cms-new-content"
          className="inline-flex items-center gap-2 bg-[#C6899E] text-[#4A2233] rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.12em] hover:bg-[#B4718C] transition-colors">
          <Plus className="w-4 h-4" /> Nuovo
        </button>
      </div>

      {items.length === 0 ? (
        <p className="text-[#9C8F94] py-10 text-center">Nessun contenuto. Crea il primo.</p>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} className="flex items-center gap-4 bg-[#FBF5F1] border border-[rgba(61,50,56,0.08)] rounded-xl p-3" data-testid={`cms-item-${it.id}`}>
              <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#F3E8E2] shrink-0">
                {it.cover_path ? <img src={mediaUrl(it.cover_path)} alt="" className="w-full h-full object-cover" /> : null}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[#3D3238] truncate">{it.title || "(senza titolo)"}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-[0.6rem] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full ${it.status === "published" ? "bg-[rgba(198,137,158,0.15)] text-[#C6899E]" : "bg-[#E9DBD3] text-[#9C8F94]"}`}>
                    {it.status === "published" ? "Pubblicato" : "Bozza"}
                  </span>
                  {it.is_premium && <span className="text-[0.6rem] uppercase tracking-[0.1em] text-[#C6899E]">Premium</span>}
                  {it.featured && <span className="text-[0.6rem] uppercase tracking-[0.1em] text-[#8C7E83]">In evidenza</span>}
                </div>
              </div>
              <button onClick={() => setEditing({ ...it, tags: (it.tags || []).join(", ") })} data-testid={`cms-edit-${it.id}`} className="p-2 text-[#8C7E83] hover:text-[#C6899E]"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(it.id)} data-testid={`cms-delete-${it.id}`} className="p-2 text-[#8C7E83] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#FFFFFF] border-[rgba(198,137,158,0.2)] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#3D3238]">{editing?.id ? "Modifica" : "Nuovo"} · {TYPE_LABELS[type]}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 pt-2">
              <Field label="Titolo"><input className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="cms-field-title" /></Field>
              <Field label="Estratto"><textarea rows={2} className={inputCls} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} data-testid="cms-field-excerpt" /></Field>

              {type === "writing" && (
                <Field label="Testo"><textarea rows={8} className={inputCls} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} data-testid="cms-field-body" /></Field>
              )}

              <FileUpload label="Copertina" value={editing.cover_path} onChange={(p) => setEditing((cur) => (cur ? { ...cur, cover_path: p } : cur))} onBusy={trackBusy} testid="cms-upload-cover" />

              <MultiFileUpload label="File / Allegati scaricabili (PDF, audio, video, immagini...)" value={editing.attachments} onChange={(v) => setEditing((cur) => (cur ? { ...cur, attachments: v } : cur))} onBusy={trackBusy} testid="cms-upload-attachments" />

              {type !== "writing" && (
                <>
                  <Field label="Tipo media">
                    <select className={inputCls} value={editing.media_kind} onChange={(e) => setEditing({ ...editing, media_kind: e.target.value })} data-testid="cms-field-mediakind">
                      {MEDIA_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </Field>
                  {editing.media_kind === "upload" ? (
                    <FileUpload label="File media" accept={accept} value={editing.media_path} onChange={(p) => setEditing((cur) => (cur ? { ...cur, media_path: p } : cur))} onBusy={trackBusy} testid="cms-upload-media" />
                  ) : editing.media_kind !== "none" ? (
                    <Field label="URL (YouTube / Vimeo / Spotify / SoundCloud)"><input className={inputCls} value={editing.media_url} onChange={(e) => setEditing({ ...editing, media_url: e.target.value })} data-testid="cms-field-mediaurl" /></Field>
                  ) : null}
                </>
              )}

              <div className="grid grid-cols-2 gap-4">
                <Field label="Tag (separati da virgola)"><input className={inputCls} value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} data-testid="cms-field-tags" /></Field>
                {type === "writing" && <Field label="Minuti di lettura"><input type="number" className={inputCls} value={editing.reading_time || ""} onChange={(e) => setEditing({ ...editing, reading_time: e.target.value ? parseInt(e.target.value) : null })} data-testid="cms-field-readingtime" /></Field>}
              </div>

              <div className="flex flex-wrap gap-6 pt-2">
                <Toggle label="Premium" checked={editing.is_premium} onChange={(v) => setEditing({ ...editing, is_premium: v })} testid="cms-toggle-premium" />
                <Toggle label="In evidenza (home)" checked={editing.featured} onChange={(v) => setEditing({ ...editing, featured: v })} testid="cms-toggle-featured" />
              </div>

              <Field label="Stato">
                <div className="flex gap-2">
                  {["draft", "published"].map((s) => (
                    <button key={s} onClick={() => setEditing({ ...editing, status: s })} data-testid={`cms-status-${s}`}
                      className={`flex items-center gap-2 text-xs uppercase tracking-[0.12em] px-4 py-2 rounded-full border transition-colors ${editing.status === s ? "border-[#C6899E] text-[#C6899E] bg-[rgba(198,137,158,0.1)]" : "border-[rgba(61,50,56,0.12)] text-[#9C8F94]"}`}>
                      {s === "published" ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                      {s === "published" ? "Pubblica" : "Bozza"}
                    </button>
                  ))}
                </div>
              </Field>

              <div className="flex justify-end gap-3 pt-4 border-t border-[rgba(61,50,56,0.08)]">
                <button onClick={() => setEditing(null)} className="text-sm text-[#9C8F94] px-4 py-2">Annulla</button>
                <button onClick={save} disabled={saving || !editing.title || uploading > 0} title={uploading > 0 ? "Attendi la fine del caricamento" : undefined} data-testid="cms-save-content"
                  className="inline-flex items-center gap-2 bg-[#C6899E] text-[#4A2233] rounded-full px-6 py-2.5 text-sm uppercase tracking-[0.12em] hover:bg-[#B4718C] transition-colors disabled:opacity-40">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Salva
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
